import { useCallback, useEffect, useState } from "react";
import { Plus, Package, Car, Percent, Pencil, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge, Field, SelectField } from "@/components/common";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LineDialog, MaterialDialog } from "@/components/billing/LineDialogs";
import { BillingTotals } from "@/components/billing/BillingTotals";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BILLING_STATUS, LINE_KINDS, LINE_BILLING, ROUNDING, fmtMin, fmtPrice } from "@/lib/constants";

const SOURCE = { client: "tarif du client", particulier: "tarif particuliers", professionnel: "tarif professionnels", manuel: "tarif modifié pour cette intervention" };

const bodyFrom = (d) => ({
  hourly_rate: d.billing.hourly_rate ?? 0, billable_minutes: d.computed.billable_manual ? d.computed.billable_minutes : null,
  labor_override: d.billing.labor_override ?? null, status: d.billing.status ?? null, invoice_number: d.billing.invoice_number || "",
  invoice_date: d.billing.invoice_date || null, lines: d.billing.lines || [],
});

const LineRow = ({ l, onEdit, onDelete }) => (
  <li className="flex flex-wrap items-center gap-2 rounded-lg border border-white/5 bg-white/[0.03] p-2.5" data-testid={`billing-line-${l.id}`}>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm text-white">{l.label} <span className="text-xs text-slate-400">· {LINE_KINDS[l.kind]}{l.reference ? ` · ${l.reference}` : ""}{l.order_id ? " · depuis commande" : ""}</span></p>
      <p className="text-xs text-slate-400">{l.quantity} × {fmtPrice(l.unit_price)} = <span className="text-slate-200">{l.kind === "remise" ? "− " : ""}{fmtPrice(l.quantity * l.unit_price)}</span></p>
    </div>
    <Badge value={l.billing} map={LINE_BILLING} />
    {l.invoiced_ref && <span className="inline-flex items-center gap-1 text-xs text-emerald-300" data-testid={`billing-line-invoiced-${l.id}`}><CheckCircle2 className="h-3.5 w-3.5" />Facture {l.invoiced_ref}</span>}
    <Button size="icon" variant="ghost" onClick={onEdit} data-testid={`billing-line-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
    <Button size="icon" variant="ghost" onClick={onDelete} data-testid={`billing-line-delete-${l.id}`}><Trash2 className="h-4 w-4 text-red-300" /></Button>
  </li>
);

const StatusBox = ({ d, save, isAdmin }) => {
  const [inv, setInv] = useState({ number: "", date: "" });
  useEffect(() => { setInv({ number: d.billing.invoice_number || "", date: d.billing.invoice_date || "" }); }, [d]);
  const status = d.billing.status || d.computed.billing_status;
  return (
    <div className="space-y-2 rounded-lg border border-white/10 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-slate-400">Statut de facturation</span>
        {status ? <Badge value={status} map={BILLING_STATUS} testid="billing-status-badge" /> : <span className="text-xs text-slate-500">Intervention pas encore réalisée</span>}
      </div>
      {isAdmin && (
        <div className="grid gap-2 sm:grid-cols-3">
          <SelectField value={status} onChange={(v) => (v === "facturee" ? save({ status: v, invoice_number: inv.number || "", invoice_date: inv.date || null }) : save({ status: v }))} options={BILLING_STATUS} placeholder="Choisir…" testid="billing-status-select" />
          <Input placeholder="N° de facture" value={inv.number} onChange={(e) => setInv({ ...inv, number: e.target.value })} data-testid="billing-invoice-number-input" />
          <Input type="date" value={inv.date} onChange={(e) => setInv({ ...inv, date: e.target.value })} data-testid="billing-invoice-date-input" />
          <Button variant="outline" className="sm:col-span-3" disabled={!inv.number.trim()} onClick={() => save({ status: "facturee", invoice_number: inv.number, invoice_date: inv.date || null })} data-testid="billing-mark-invoiced-button">Enregistrer comme facturée</Button>
        </div>
      )}
    </div>
  );
};

const TimeBox = ({ d, save }) => {
  const c = d.computed;
  const [rate, setRate] = useState("");
  const [mins, setMins] = useState("");
  const [labor, setLabor] = useState("");
  useEffect(() => {
    setRate(d.billing.hourly_rate ?? 0);
    setMins(c.billable_manual ? c.billable_minutes : "");
    setLabor(d.billing.labor_override ?? "");
  }, [d, c.billable_manual, c.billable_minutes]);
  const commit = (patch) => save(patch);
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {[["Chronométré", fmtMin(c.tracked_minutes), "billing-tracked"], ["Retenu", fmtMin(c.retained_minutes), "billing-retained"],
        [`Proposé (${ROUNDING[d.billing.rounding] || "arrondi"})`, fmtMin(c.proposed_billable_minutes), "billing-proposed"],
        ["Facturable", fmtMin(c.billable_minutes), "billing-billable"]].map(([k, v, t]) => (
        <div key={k}><p className="text-xs text-slate-400">{k}</p><p className="font-mono-dt text-lg text-white" data-testid={t}>{v}</p></div>
      ))}
      <Field label="Facturable (min, vide = proposé)">
        <Input type="number" min={0} inputMode="numeric" value={mins} placeholder={String(c.proposed_billable_minutes)} onChange={(e) => setMins(e.target.value)}
          onBlur={() => commit({ billable_minutes: mins === "" ? null : Number(mins) })} data-testid="billing-billable-input" />
      </Field>
      <Field label="Tarif horaire (€/h)">
        <Input type="number" min={0} step="0.01" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)}
          onBlur={() => Number(rate) !== d.billing.hourly_rate && commit({ hourly_rate: Number(rate) || 0 })} data-testid="billing-rate-input" />
        <span className="text-[11px] text-slate-500">{SOURCE[d.billing.rate_source] || ""}</span>
      </Field>
      <Field label="Main-d'œuvre (€, vide = calculée)" className="col-span-2">
        <Input type="number" min={0} step="0.01" inputMode="decimal" value={labor} placeholder={String(c.labor_calculated)} onChange={(e) => setLabor(e.target.value)}
          onBlur={() => commit({ labor_override: labor === "" ? null : Number(labor) })} data-testid="billing-labor-input" />
      </Field>
    </div>
  );
};

export const BillingPanel = ({ interventionId, onChanged, mode = "site" }) => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [line, setLine] = useState(null);
  const [material, setMaterial] = useState(false);
  const load = useCallback(() => api.get(`/interventions/${interventionId}/billing`).then((r) => setD(r.data)), [interventionId]);
  useEffect(() => { load(); }, [load]);
  const save = async (patch, msg = "Facturation mise à jour") => {
    try {
      const { data } = await api.put(`/interventions/${interventionId}/billing`, { ...bodyFrom(d), ...patch });
      setD(data);
      toast.success(msg);
      onChanged?.();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };
  if (!d) return null;
  const lines = d.billing.lines || [];
  const saveLine = async (l) => {
    const next = l.id ? lines.map((x) => (x.id === l.id ? l : x)) : [...lines, l];
    if (await save({ lines: next }, "Ligne enregistrée")) setLine(null);
  };
  const addOrders = async (items) => {
    const next = [...lines];
    items.forEach(({ order, quantity, unit_price, billing }) => {
      const i = next.findIndex((x) => x.order_id === order.id && x.billing === billing && x.unit_price === unit_price);
      if (i >= 0) next[i] = { ...next[i], quantity: next[i].quantity + quantity };
      else next.push({ kind: "materiel", label: order.item, reference: order.reference, quantity, unit_price, billing, order_id: order.id, description: "" });
    });
    if (await save({ lines: next }, "Matériel ajouté")) setMaterial(false);
  };
  const travel = d.settings.travel_options || [];
  return (
    <section className="space-y-4 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="billing-panel">
      <h2 className="font-display text-base font-semibold text-white md:text-lg">Facturation</h2>
      <StatusBox d={d} save={save} isAdmin={user?.role === "admin"} />
      <TimeBox d={d} save={save} />
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        <Button className="dt-glow col-span-2 h-12" onClick={() => setMaterial(true)} data-testid="billing-add-material-button"><Package className="mr-2 h-4 w-4" />Ajouter du matériel utilisé</Button>
        {mode === "site" && <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="outline" className="h-12" data-testid="billing-add-travel-button"><Car className="mr-2 h-4 w-4" />Déplacement</Button></DropdownMenuTrigger>
          <DropdownMenuContent className="border-cyan-400/20 bg-[#0F1F3D]">
            {travel.map((t, i) => (
              <DropdownMenuItem key={i} data-testid={`billing-travel-option-${i}`}
                onClick={() => setLine({ kind: "deplacement", label: `Déplacement ${t.label}`, quantity: 1, unit_price: t.price ?? "" })}>
                {t.label} · {t.price == null ? "sur devis" : fmtPrice(t.price)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>}
        <Button variant="outline" className="h-12" onClick={() => setLine({ kind: "prestation", quantity: 1, unit_price: "" })} data-testid="billing-add-line-button"><Plus className="mr-2 h-4 w-4" />Autre ligne</Button>
        <Button variant="outline" className="h-12" onClick={() => setLine({ kind: "remise", label: "Remise commerciale", quantity: 1, unit_price: "" })} data-testid="billing-add-discount-button"><Percent className="mr-2 h-4 w-4" />Remise</Button>
      </div>
      <ul className="space-y-2" data-testid="billing-lines">
        {!lines.length && <li className="text-sm text-slate-400">Aucun élément financier pour l'instant.</li>}
        {lines.map((l) => <LineRow key={l.id} l={l} onEdit={() => setLine(l)} onDelete={() => save({ lines: lines.filter((x) => x.id !== l.id) }, "Ligne supprimée")} />)}
      </ul>
      <BillingTotals c={d.computed} />
      <LineDialog line={line} onClose={() => setLine(null)} onSave={saveLine} />
      <MaterialDialog interventionId={interventionId} open={material} onClose={() => setMaterial(false)} onAdd={addOrders}
        onManual={() => { setMaterial(false); setLine({ kind: "materiel", quantity: 1, unit_price: "" }); }} />
    </section>
  );
};
